import React, { useEffect, useState } from "react"
import esb, { RequestBodySearch } from "elastic-builder"
import { Common } from "lib/types"
import Filter from "../Ui/Table/Filter/Filter"
import CheckboxSimple from "../Ui/Form/CheckboxSimple"
import { useLazyQuery, useQuery } from "@apollo/client"
import ALL_TAG_KEYS from "../../graphql/queries/tags/AllTagKeys"
import SingleSelectSimple from "../Ui/Form/SingleSelectSimple"
import TAG_VALUES from "../../graphql/queries/tags/TagValues"
import MultiSelect from "../Ui/Form/MultiSelect"
import { MultiSelectWrapper, TagsWrapper } from "./ProductFilters.styled"
import { ReactComponent as TagsIcon } from "images/icons/tag.svg"
import { handleErrorMessages } from "helpers/errors"
import {
  ActiveFilters,
  ActiveListListItem,
  ClearButton,
  RemoveButton
} from "../Ui/Table/Filter/Filter.styled"
import { v4 as uuidv4 } from "uuid"

type Props = {
  setSearchQuery: (input: string) => void
  loading: boolean
  searchQuery: string
  setEsbQuery: (query: RequestBodySearch) => void
  productFilter: Common.ProductFilter
  setProductFilter: (filter: Common.ProductFilter) => void
  numberOfHitsOption: string[]
  defaultProductFilter: Common.ProductFilter
  totalHits: number | undefined
}

const ProductFilters = ({
  setSearchQuery,
  loading,
  searchQuery,
  setEsbQuery,
  productFilter,
  setProductFilter,
  numberOfHitsOption,
  defaultProductFilter,
  totalHits
}: Props) => {
  const [tagKey, setTagKey] = useState(productFilter.tags?.key || "")
  const [tagValueOptions, setTagValueOptions] = useState<Common.Option[]>([])
  const [tagValues, setTagValues] = useState<string[]>(productFilter.tags?.value || [])
  const isFilterActive = () => {
    const { isActive, isArchived, tags } = productFilter
    return !isActive || isArchived || Boolean(tags?.key && tags?.value?.length > 0)
  }

  const [getTags] = useLazyQuery(TAG_VALUES, {
    onCompleted: (data) => {
      setTagValueOptions(
        data?.tagValues?.values.map((value: Common.Option) => ({
          value,
          label: value
        }))
      )
    },
    onError: (err) => {
      handleErrorMessages(err)
    }
  })

  const { data } = useQuery(ALL_TAG_KEYS)

  const totalFilters =
    (productFilter.isActive ? 0 : 1) +
    (productFilter.isArchived ? 1 : 0) +
    (productFilter.tags?.key && productFilter.tags?.value?.length > 0 ? productFilter.tags?.value?.length : 0)

  const getTagKeyOptions = () =>
    data?.tagKeys?.keys.map((key: string) => ({ label: key, value: key }))
  const setIsActive = (value: boolean) =>
    setProductFilter({ ...productFilter, ...{ isActive: value } })
  const setIsArchived = (value: boolean) =>
    setProductFilter({ ...productFilter, ...{ isArchived: value } })
  const setNumberOfHits = (value: string) =>
    setProductFilter({ ...productFilter, ...{ numberOfHits: value } })
  const setProductTagKey = (value: string) => {
    setTagKey(value)
    setProductFilter({ ...productFilter, tags: { key: value, value: [] } })
    setTagValues([])
  }
  const setProductTagValues = (values: string[]) => {
    setTagValues(values)
    setProductFilter({ ...productFilter, tags: { ...productFilter.tags, value: values } })
  }

  useEffect(() => {
    const boolQuery = esb.boolQuery()
    boolQuery.must(esb.termsQuery("isActive", productFilter.isActive))
    boolQuery.must(esb.termsQuery("isArchived", productFilter.isArchived))
    boolQuery.must([
      esb
        .queryStringQuery(searchQuery.trim() + "*")
        .defaultOperator("AND")
        .analyzeWildcard(true)
    ])

    if (productFilter.tags?.key && productFilter.tags?.value?.length > 0) {
      boolQuery.must(
        esb.nestedQuery(
          esb
            .boolQuery()
            .must(esb.termQuery("tags.key", productFilter.tags?.key))
            .must(esb.termsQuery("tags.value", productFilter.tags?.value)),
          "tags"
        )
      )
    }

    setEsbQuery(esb.requestBodySearch().query(boolQuery))
  }, [productFilter, searchQuery])

  useEffect(() => {
    if (tagKey) {
      getTags({ variables: { key: tagKey, prefix: "" } }).then((response) => {
        const result = response?.data?.tagValues?.values.map((value: string) => ({
          value,
          label: value
        }))
        setTagValueOptions(result || [])
      })
    } else {
      setTagValueOptions([])
    }
  }, [tagKey])

  const resetFilter = () => {
    setProductFilter(defaultProductFilter)
    setTagKey("")
    setTagValues([])
  }

  return (
    <>
      <Filter
        isFilterActive={isFilterActive}
        setNumberOfHits={setNumberOfHits}
        numberOfHitsOptions={numberOfHitsOption}
        selectedNumberOfHits={productFilter?.numberOfHits}
        resetFilter={resetFilter}
        totalHits={totalHits}
        totalFilters={totalFilters}
        searchOptions={{
          handleChange: (input: string) => {
            setSearchQuery(input)
          },
          loading: loading,
          defaultValue: searchQuery
        }}
      >
        <CheckboxSimple
          name="active"
          label="active"
          setValue={setIsActive}
          value={productFilter.isActive}
        />
        <CheckboxSimple
          name="archived"
          label="archived"
          setValue={setIsArchived}
          value={productFilter.isArchived}
        />

        <TagsWrapper>
          <span>
          <TagsIcon className={"hideOnSmallScreens"} />
          <SingleSelectSimple
            options={getTagKeyOptions()}
            setValue={setProductTagKey}
            value={tagKey ? { label: tagKey, value: tagKey } : null}
            placeholder="Select tag key"
          />
            </span>
          <MultiSelectWrapper>
            <MultiSelect
              options={tagValueOptions}
              setValue={setProductTagValues}
              value={tagValues.map((val) => ({ label: val, value: val }))}
              placeholder="Select tag values"
              disabled={!tagKey}
              returnValueOnly={true}
            />
          </MultiSelectWrapper>
        </TagsWrapper>
      </Filter>
      {totalFilters > 0 && (
        <ActiveFilters>
          {productFilter?.tags?.value?.map((f) => (
            <ActiveListListItem
              key={uuidv4()}
              onClick={() =>
                setProductTagValues(productFilter?.tags?.value?.filter((o) => o !== f))
              }
            >
              <TagsIcon /> {f} <RemoveButton />
            </ActiveListListItem>
          ))}
          {!productFilter.isActive && (
            <ActiveListListItem onClick={() => setIsActive(true)}>
              Inactive <RemoveButton />
            </ActiveListListItem>
          )}
          {productFilter.isArchived && (
            <ActiveListListItem onClick={() => setIsArchived(false)}>
              Archived <RemoveButton />
            </ActiveListListItem>
          )}
          <ClearButton
            handleClick={(e) => {
              e.stopPropagation()
              resetFilter()
            }}
          >
            Reset
          </ClearButton>
        </ActiveFilters>
      )}
    </>
  )
}
export default ProductFilters
